import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios"

import {
  ResponseData,
  PropertyDataV2,
  GetPropertiesTestRequestParams,
  GetPropertiesGetRequestParams,
  ApiStatus,
} from "./common"
export interface GetPropertiesTestRequest extends Partial<AxiosRequestConfig> {
  params: GetPropertiesTestRequestParams
}

export interface PropertiesTestInterface {
  content: PropertyDataV2[]
  nearbyProperties: PropertyDataV2[]
  propertyCount: number
  mapMarkerFitToView: boolean
  priceDropAlertId: string | null
  showPDAToggle?: boolean | null
}
export interface GetPropertiesTestResponseData extends ResponseData {
  data: PropertiesTestInterface
}
export interface GetPropertiesTestResponse extends AxiosResponse {
  data: GetPropertiesTestResponseData
}

// --------------------------------------------------------------------------------------------

export interface GetPropertiesGetRequest extends Partial<AxiosRequestConfig> {
  params: Record<string, string>
}

export function getPropertiesGetApi(
  { params }: GetPropertiesGetRequest,
  axiosInstance: AxiosInstance,
) {
  return axiosInstance({
    params: params,
    method: "GET",
    url: `/search/new`,
  }) as Promise<GetPropertiesTestResponse>
}

// --------------------------------------------------------------------------------------------

export interface GetPropertiesCountRequest extends Partial<AxiosRequestConfig> {
  params: Pick<
    GetPropertiesTestRequestParams,
    | "adultCount"
    | "attachmentsCount"
    | "bounds"
    | "childrenCount"
    | "endDate"
    | "filters"
    | "isMapDragged"
    | "lat"
    | "lng"
    | "name"
    | "noOfDays"
    | "petAllowed"
    | "placeId"
    | "placeType"
    | "startDate"
    | "whimstayPlus"
    | "toggleOn"
    | "propertyConfigTypeId"
    | "sort"
  >
}
export interface CountApiListOfAllProperties {
  count: number
  id: string
  latitude: number
  longitude: number
}
export interface GetPropertiesCountResponseData extends ResponseData {
  data: {
    count: number
    originalCount: number
    data: CountApiListOfAllProperties[] | null
    withPolygon: boolean
    expandedPropertiesReturned: number | null
    expandedRadiusMiles: number | null
    message?: string
    multiUnitPropertyCount?: number | null
    maxPrice?: number | null
    minPrice?: number | null
    totalHostBadgeCount?: number | null
  }
}
export interface GetPropertiesCountResponse extends AxiosResponse {
  data: GetPropertiesCountResponseData
}

// ---------------------------

export interface GetPropertiesCountGETRequest
  extends Partial<AxiosRequestConfig> {
  params: GetPropertiesGetRequestParams
}

export function getPropertiesCountGETApi(
  { params }: GetPropertiesCountGETRequest,
  axiosInstance: AxiosInstance,
) {
  return axiosInstance({
    params,
    method: "GET",
    url: "/search/new/count",
  }) as Promise<GetPropertiesCountResponse>
}

// -----------------------------------------------------------
export interface SearchDotPropertyCardResponseData extends ResponseData {
  data: PropertyDataV2 | PropertyDataV2[]
}
export interface SearchDotPropertyCardResponse extends AxiosResponse {
  data: SearchDotPropertyCardResponseData
}

export interface SearchDotPropertyCardParams {
  id: string
  noOfDays: number
  startDate: string
  numberOfGuest: number
  petAllowed: boolean
  isMultiUnit?: boolean
  allInPriceToggle?: boolean
  isgoogle?: boolean
  page?: number
  limit?: number
}
export interface SearchDotPropertyCardRequest
  extends Partial<AxiosRequestConfig> {
  params: SearchDotPropertyCardParams
}

export function SearchDotPropertyCardApi(
  { params }: SearchDotPropertyCardRequest,
  axiosInstance: AxiosInstance,
) {
  return axiosInstance({
    data: params,
    method: "post",
    url: "/search/property-card",
  }) as Promise<SearchDotPropertyCardResponse>
}

// ---------------GET MAP COORDINATES DATA -------------------

export interface Coordinate {
  lat: number
  lng: number
}

export interface GeometryType {
  type: string
  coordinates: Coordinate[][]
}

export interface MapCoordinatesResponse {
  geojson: GeometryType
  ne_lan: number
  ne_lat: number
  sw_lan: number
  sw_lat: number
}

export interface GetMapCoordinatesResponseData {
  data: MapCoordinatesResponse
}

export interface GetMapCoordinatesResponse extends AxiosResponse {
  data: GetMapCoordinatesResponseData
  apiStatus: ApiStatus
}

export interface GetMapCoordinatesRequest extends Partial<AxiosRequestConfig> {
  params: {
    name: string
  }
}

// ---- Request Params (derived from your curl) ----
export interface GetPriceBarChartParams {
  multiUnitCollapsedEnabled: boolean
  FeaturesAndAmenities: string
  Location: string
  adultCount: number
  bounds: string
  childAllowed: boolean
  suitableChild: boolean
  childrenCount: number
  endDate: string
  eventAllowed: boolean
  partyAllowed: boolean
  isSearchingInMap: boolean
  lat: number
  limit: number
  lng: number
  maxPrice: number
  minBathRoom: number
  minBedRoom: number
  minBed: number
  minPrice: number
  name: string
  noOfDays: number
  numberOfGuest: number
  page: number
  petAllowed: boolean
  placeId: string
  placeType: string
  smokeAllowed: boolean
  sortBy: string
  startDate: string
  toggleOn: boolean
  mapDragged: boolean
  withPolygon: boolean
  cancellable: boolean
  nearByProperties: boolean
  pviVersion: string
  allInPriceToggle: boolean
  isgoogle: boolean
}

// ---- Request Type ----
export interface GetPriceBarChartRequest extends Partial<AxiosRequestConfig> {
  params: GetPriceBarChartParams
}

// ---- Expected Response Shape (adjust if needed) ----

export interface PriceBarChartData {
  count: number
  minPrice: number
  maxPrice: number
}

export interface GetPriceBarChartResponseData extends ResponseData {
  data: PriceBarChartData[]
}

export interface GetPriceBarChartResponse extends AxiosResponse {
  data: GetPriceBarChartResponseData
}

export function getMapCoordinatesApi(
  { params }: GetMapCoordinatesRequest,
  axiosInstance: AxiosInstance,
) {
  return axiosInstance({
    method: "get",
    url: `/search/polygon?name=${params.name}`,
  }) as Promise<GetMapCoordinatesResponse>
}

// ---- API FUNCTION (matches your style exactly) ----
export function getPriceBarChartApi(
  { params }: GetPriceBarChartRequest,
  axiosInstance: AxiosInstance,
) {
  return axiosInstance({
    params,
    method: "GET",
    url: "/search/price-bar-chart",
  }) as Promise<GetPriceBarChartResponse>
}
